import React, { useState } from "react";
import { useAllOrdersContext } from "../../hooks/useContext/useAllOrdersContext";
import { useAuthContext } from "../../hooks/useContext/useAuthContext";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { CircularProgress, Typography } from "@mui/material";
import ErrorBox from "../Shared/ErrorBox";
import { Tabs, Tab } from '@mui/material';
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "../../hooks/useTranslation";


const OrdersComponentController = ({ tabValue: externalTabValue, onTabChange }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const [error, setError] = useState(null);
  const [internalTabValue, setInternalTabValue] = useState('pending');
  const tabValue = externalTabValue ?? internalTabValue;
  const { allOrders, allOrdersDispatch } = useAllOrdersContext();
  const pendingOrders = allOrders ? allOrders.filter(order => order.status === 'Pending') : [];
  const [loadingStates, setLoadingStates] = useState({});
  // const [smsLoadingStates, setSmsLoadingStates] = useState({});
  // const [smsErrorStates, setSmsErrorStates] = useState({});
  const [readySmsLoading, setReadySmsLoading] = useState({});
const [callSmsLoading, setCallSmsLoading] = useState({});
const [readySmsError, setReadySmsError] = useState({});
const [callSmsError, setCallSmsError] = useState({});
const [reviewOpenStates, setReviewOpenStates] = useState({});

  const toggleReview = (orderId) => {
    setReviewOpenStates((prev) => ({ ...prev, [orderId]: !prev[orderId] }));
  };

  const getOrderNumber = (order) => {
    if (Number.isInteger(order?.dailyOrderNumber)) {
      return String(order.dailyOrderNumber).padStart(3, '0');
    }
    return String(order?._id || '').slice(-6).toUpperCase();
  };

  const translateItemSize = (itemSize) => {
    if (!itemSize) return itemSize;
    const sizeKeyMap = {
      'Small': 'small',
      'Large': 'large',
      'One Size': 'oneSize',
      'small': 'small',
      'large': 'large',
      'oneSize': 'oneSize',
      'one size': 'oneSize',
    };
    const key = sizeKeyMap[itemSize] || sizeKeyMap[String(itemSize).trim()];
    return key ? t(key) : itemSize;
  };

  const isOneSize = (itemSize) => {
    if (!itemSize) return false;
    const normalized = String(itemSize).trim().toLowerCase();
    return normalized === 'one size' || normalized === 'onesize' || normalized === 'oneSize'.toLowerCase() || normalized === 'jeden rozmiar';
  };

  const getLanguageFlag = (language) => {
    const isPolish = String(language || '').toLowerCase() === 'pl';
    return isPolish
      ? { src: `${process.env.PUBLIC_URL}/images/flags/pl.svg`, alt: 'Polish flag' }
      : { src: `${process.env.PUBLIC_URL}/images/flags/us.svg`, alt: 'US flag' };
  };

  const handleOrderListChange = (event, newValue) => {
    if (typeof onTabChange === 'function') {
      onTabChange(newValue);
      return;
    }
    setInternalTabValue(newValue);
  };

  const updateOrder = async (orderId, updateFields) => {
    setError(null);
    setLoadingStates(prevStates => ({ ...prevStates, [orderId]: true }));
    try {
      const response = await fetch(process.env.REACT_APP_SERVER_URL + "/api/orders/pendingOrders", {
        method: "PUT",
        body: JSON.stringify({ _id: orderId, ...updateFields }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.token}`,
        },
      });
      const json = await response.json();
      if (response.ok) {
        setLoadingStates(prevStates => ({ ...prevStates, [orderId]: false }));
        allOrdersDispatch({ type: "UPDATE_ORDERS", payload: json });
      }
    } catch (error) {
      setError(error);
      setLoadingStates(prevStates => ({ ...prevStates, [orderId]: false }));
    }
  };

  const updateOrderStatus = async (orderId) => {
    return updateOrder(orderId, { status: 'Complete' });
  };

  const markAddedToFiscalPrinter = async (orderId) => {
    return updateOrder(orderId, { addedToFiscalPrinter: true });
  };

// NEW: Send "food is ready" SMS using Twilio backend route
  const sendReadyText = async (order) => {
    setError(null);
    setReadySmsLoading(prev => ({ ...prev, [order._id]: true }));

    // IMPORTANT: trigger the in-app update/notification first.
    // SMS delivery can fail for non-PL numbers, but that should not block the status update.
    await updateOrderStatus(order._id);

    try {
      const response = await fetch(
        process.env.REACT_APP_SERVER_URL + "/api/orders/sendTextReminder",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
          body: JSON.stringify({ order }),
        }
      );

      const json = await response.json();

      if (response.ok) {
        setReadySmsError(prev => ({ ...prev, [order._id]: null }));
        alert(`Text sent to ${order.phoneNumber}`);
      } else {
        setReadySmsError(prev => ({ ...prev, [order._id]: json.error || "Failed to send SMS" }));
      }
    } catch (err) {
      setReadySmsError(prev => ({ ...prev, [order._id]: "Failed to send SMS. Please try again." }));
    } finally {
      setReadySmsLoading(prev => ({ ...prev, [order._id]: false }));
    }

  };

  // NEW: Send "food is ready" SMS using Twilio backend route
  const sendCallToCounterText = async (order) => {
    setError(null);
    // setSmsLoadingStates((prev) => ({ ...prev, [order._id]: true }));
    setCallSmsLoading(prev => ({ ...prev, [order._id]: true }));

    try {
      const response = await fetch(
        process.env.REACT_APP_SERVER_URL + "/api/orders/sendCallToCounterText",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
          body: JSON.stringify({ order }),
        }
      );

      const json = await response.json();

      if (response.ok) {
        setCallSmsError(prev => ({ ...prev, [order._id]: null }));
        // setSmsErrorStates(prev => ({ ...prev, [order._id]: null }));
        alert(`Text sent to ${order.phoneNumber}`);
      } else {
        setCallSmsError(prev => ({ ...prev, [order._id]: json.error || "Failed to send SMS" }));
        // setSmsErrorStates(prev => ({ ...prev, [order._id]: json.error || "Failed to send SMS" }));
      }
    } catch (err) {
      // setSmsErrorStates(prev => ({ ...prev, [order._id]: "Failed to send SMS. Please try again." }));
      setCallSmsError(prev => ({ ...prev, [order._id]: "Failed to send SMS. Please try again." }));
    } finally {
      // setSmsLoadingStates((prev) => ({ ...prev, [order._id]: false }));
      setCallSmsLoading(prev => ({ ...prev, [order._id]: false }));
    }

  };


// Display loading state or similar message
  if (!allOrders) {
    return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", paddingTop: '10vh' }}>
      <CircularProgress />
    </Box>
    );
  };

  return (
      <Box sx={{ "& > :not(style)": { m: 1, width: "100%" }, display: "flex", flexDirection: "column", alignItems: "center", p: 2 }}>
      <Tabs value={tabValue} onChange={handleOrderListChange} centered>
        <Tab value={'pending'} label={`${t('newOrders')} (${pendingOrders.length})`} wrapped />
        <Tab value="all" label={`${t('allOrders')} (${allOrders.length})`} wrapped />
      </Tabs>

      {tabValue === 'pending' && pendingOrders.map((order) => (
        <div key={order._id} style={{
            margin: '20px',
            padding: '20px',
            border: '1px solid #ddd',
            borderRadius: '12px',
            backgroundColor: '#fafafa',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
          }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <Typography variant="h6" component="p" style={{ fontWeight: 'bold', marginBottom: '10px', color: '#333' }}>
                {t('orderTime')}: {new Date(order.createdAt).toLocaleString('en-GB', { 
                  day: '2-digit', 
                  month: '2-digit', 
                  year: 'numeric', 
                  hourCycle: 'h23', 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}
              </Typography>
              <Typography variant="subtitle1" component="p" style={{ marginBottom: '8px', color: '#555' }}>
                <strong>{t('orderNumber')}:</strong> {getOrderNumber(order)}
              </Typography>
              <Typography variant="subtitle1" component="p" style={{ marginBottom: '8px', color: '#555' }}>
                <strong>{t('orderName')}:</strong> {order.name}
              </Typography>
              <Typography variant="subtitle1" component="p" style={{ marginBottom: '8px', color: '#555' }}>
                <strong>{t('phoneNumber')}:</strong> {order.phoneNumber}
              </Typography>
              <Typography variant="subtitle1" component="p" style={{ marginBottom: '8px', color: '#555' }}>
                <img
                  src={getLanguageFlag(order.language).src}
                  alt={getLanguageFlag(order.language).alt}
                  width="20"
                  height="15"
                  style={{ display: 'inline-block', verticalAlign: 'middle', borderRadius: '2px' }}
                />
              </Typography>
              {/* <Typography variant="subtitle1" component="p" style={{ marginBottom: '8px', color: '#555' }}>
                <strong>Email:</strong> {order.email}
              </Typography> */}
              {/* <Typography variant="subtitle1" component="p" style={{ marginBottom: '16px', color: '#555' }}>
                <strong>Payment Status:</strong> {order.paidStatus ? 'Paid' : 'Unpaid'}
              </Typography> */}
              <Typography variant="subtitle1" component="p" style={{ marginBottom: '16px', fontWeight: 'bold', color: '#333' }}>
                <strong>{t('totalPrice')}:</strong> {`${order.totalPrice} zł`}
              </Typography>

              <Typography variant="subtitle1" component="p" style={{ marginBottom: '8px', color: '#555' }}>
                <strong>{t('addedToFiscalPrinter')}:</strong> {order.addedToFiscalPrinter ? t('yes') : t('no')}
              </Typography>
            </div>
            <div style={{ flex: 1, borderLeft: '1px solid #ddd', paddingLeft: '20px' }}>
              <ul style={{ paddingLeft: '20px', marginBottom: '16px', color: '#444' }}>
                {order.orderItems.map((oi, index) => (
                  <li key={index} style={{ marginBottom: '12px' }}>
                    <Typography variant="body1" component="p" style={{ fontWeight: 'bold' }}>
                      x{oi.itemQuantity} - {(oi.itemName ? t(oi.itemName) : '') || oi.itemName}
                    </Typography>

                    {!isOneSize(oi.itemSize) && (
                      <Typography variant="body2" component="p" style={{ fontStyle: 'italic', color: '#777' }}>
                        {t('size')}: {translateItemSize(oi.itemSize)}
                      </Typography>
                    )}

                    {oi.specialInstructions && (
                      <Typography variant="body2" component="p" style={{ fontStyle: 'italic', color: '#777' }}>
                        {t('instructions')}: {oi.specialInstructions}
                      </Typography>
                    )}

                  </li>
                ))}
              </ul>
            </div>
          </div>

          {loadingStates[order._id] && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", padding: '1%' }}>
              <CircularProgress />
            </Box>
          )}
          { error && 
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", padding: '1%' }}>
              <ErrorBox error={error} color="error" />
            </Box>
          }
          <Button
            variant="outlined"
            type="button"
            // disabled={smsLoadingStates[order._id]}
            disabled={readySmsLoading[order._id]}
            onClick={() => updateOrderStatus(order._id)}
            sx={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              cursor: "pointer",
              color: theme.palette.common.white,
              backgroundColor: theme.palette.common.offBlack,
              ':hover': { bgcolor: theme.palette.secondary.grey },
              borderRadius: "8px",
              padding: "6px 14px",
              textTransform: "none",
              minWidth: 'auto',
            }}
          >
          {t('hide')}
          </Button>

          <Button
            variant="contained"
            type="button"
            disabled={loadingStates[order._id] || Boolean(order.addedToFiscalPrinter)}
            onClick={() => markAddedToFiscalPrinter(order._id)}
            sx={{
              marginTop: '16px',
              cursor: 'pointer',
              color: theme.palette.common.white,
              backgroundColor: theme.palette.primary.main,
              ':hover': { bgcolor: theme.palette.secondary.main },
              borderRadius: '8px',
              padding: '8px 20px',
              textTransform: 'none',
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
            }}
          >
            {t('addedToFiscalPrinter')}
          </Button>

          <Button
            variant="contained"
            type="button"
            // disabled={smsLoadingStates[order._id]}
            disabled={callSmsLoading[order._id]}
            onClick={() => sendCallToCounterText(order)}
            sx={{
              cursor: 'pointer',
              color: theme.palette.common.black,
              backgroundColor: '#ffe066', // yellow/caution
              borderColor: '#ffd600',
              ':hover': { bgcolor: '#ffd600', borderColor: '#ffc107' },
              marginTop: '16px',
              borderRadius: '8px',
              padding: '8px 20px',
              textTransform: 'none',
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
            }}
          >
            {callSmsLoading[order._id] ? t('sendingCallToCounter') : t('callToCounter')}
          </Button>
          {callSmsError[order._id] && <ErrorBox error={callSmsError[order._id]} color="error" />}
          <Button
            variant="contained"
            type="button"
            // disabled={smsLoadingStates[order._id]}
            disabled={readySmsLoading[order._id]}
            onClick={() => sendReadyText(order)}
            sx={{
              marginTop: '16px',
              cursor: "pointer",
              color: theme.palette.common.white,
              backgroundColor: theme.palette.primary.main,
              ':hover': { bgcolor: theme.palette.secondary.main },
              borderRadius: "8px",
              padding: "8px 20px",
              textTransform: "none",
            }}
          >
            {readySmsLoading[order._id] ? t('sendingText') : t('sendTextFoodReady')}
          </Button>
          {readySmsError[order._id] && <ErrorBox error={readySmsError[order._id]} color="error" />}

          {order?.customerFeedback?.text && (
            <>
              <Button
                variant="outlined"
                type="button"
                onClick={() => toggleReview(order._id)}
                sx={{
                  marginTop: '12px',
                  borderRadius: '8px',
                  textTransform: 'none',
                }}
              >
                {reviewOpenStates[order._id] ? t('hideReview') : t('viewReview')}
              </Button>
              {reviewOpenStates[order._id] && (
                <Box sx={{ mt: 1.5, p: 1.5, border: `1px solid ${theme.palette.component.borderColor}`, borderRadius: 1.5 }}>
                  <Typography variant="body2">{order.customerFeedback.text}</Typography>
                </Box>
              )}
            </>
          )}
        </div>
      ))}

      {tabValue === 'all' && allOrders.map((order) => (
        <div key={order._id} style={{
            margin: '20px',
            padding: '20px',
            border: '1px solid #ddd',
            borderRadius: '12px',
            backgroundColor: '#fafafa',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            transition: 'all 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <Typography variant="h6" component="p" style={{ fontWeight: 'bold', marginBottom: '10px', color: '#333' }}>
                {t('orderTime')}: {new Date(order.createdAt).toLocaleString('en-GB', { 
                  day: '2-digit', 
                  month: '2-digit', 
                  year: 'numeric', 
                  hourCycle: 'h23', 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}
              </Typography>
              <Typography variant="subtitle1" component="p" style={{ marginBottom: '8px', color: '#555' }}>
                <strong>{t('orderNumber')}:</strong> {getOrderNumber(order)}
              </Typography>
              <Typography variant="subtitle1" component="p" style={{ marginBottom: '8px', color: '#555' }}>
                <strong>{t('orderName')}:</strong> {order.name}
              </Typography>
              <Typography variant="subtitle1" component="p" style={{ marginBottom: '8px', color: '#555' }}>
                <strong>{t('phoneNumber')}:</strong> {order.phoneNumber}
              </Typography>
              <Typography variant="subtitle1" component="p" style={{ marginBottom: '8px', color: '#555' }}>
                <img
                  src={getLanguageFlag(order.language).src}
                  alt={getLanguageFlag(order.language).alt}
                  width="20"
                  height="15"
                  style={{ display: 'inline-block', verticalAlign: 'middle', borderRadius: '2px' }}
                />
              </Typography>
              {/* <Typography variant="subtitle1" component="p" style={{ marginBottom: '8px', color: '#555' }}>
                <strong>Email:</strong> {order.email}
              </Typography> */}
              {/* <Typography variant="subtitle1" component="p" style={{ marginBottom: '16px', color: '#555' }}>
                <strong>Payment Status:</strong> {order.paidStatus ? 'Paid' : 'Unpaid'}
              </Typography> */}
              <Typography variant="subtitle1" component="p" style={{ marginBottom: '16px', fontWeight: 'bold', color: '#333' }}>
                <strong>{t('totalPrice')}:</strong> {`${order.totalPrice} zł`}
              </Typography>

              <Typography variant="subtitle1" component="p" style={{ marginBottom: '8px', color: '#555' }}>
                <strong>{t('addedToFiscalPrinter')}:</strong> {order.addedToFiscalPrinter ? t('yes') : t('no')}
              </Typography>
            </div>
            <div style={{ flex: 1, borderLeft: '1px solid #ddd', paddingLeft: '20px' }}>
              <ul style={{ paddingLeft: '20px', marginBottom: '16px', color: '#444' }}>
                {order.orderItems.map((oi, index) => (
                  <li key={index} style={{ marginBottom: '12px' }}>
                    <Typography variant="body1" component="p" style={{ fontWeight: 'bold' }}>
                     x{oi.itemQuantity} - {(oi.itemName ? t(oi.itemName) : '') || oi.itemName}
                    </Typography>

                    {oi.specialInstructions && (
                      <Typography variant="body2" component="p" style={{ fontStyle: 'italic', color: '#777' }}>
                        {t('instructions')}: {oi.specialInstructions}
                      </Typography>
                    )}

                    {!isOneSize(oi.itemSize) && (
                      <Typography variant="body2" component="p" style={{ fontStyle: 'italic', color: '#777' }}>
                        {t('size')}: {translateItemSize(oi.itemSize)}
                      </Typography>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <Box sx={{display: "flex", justifyContent: "center"}}>
            <Typography variant="subtitle1" component="p">{`${t('status')}: ${order.status === 'Pending' ? t('pending') : t('complete')}`}</Typography>
          </Box>

          <Box sx={{display: "flex", justifyContent: "center", marginTop: '12px' }}>
            <Button
              variant="contained"
              type="button"
              disabled={loadingStates[order._id] || Boolean(order.addedToFiscalPrinter)}
              onClick={() => markAddedToFiscalPrinter(order._id)}
              sx={{
                cursor: 'pointer',
                color: theme.palette.common.white,
                backgroundColor: theme.palette.primary.main,
                ':hover': { bgcolor: theme.palette.secondary.main },
                borderRadius: '8px',
                padding: '8px 20px',
                textTransform: 'none',
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
              }}
            >
              {t('addedToFiscalPrinter')}
            </Button>
          </Box>

          {order?.customerFeedback?.text && (
            <Box sx={{display: "flex", flexDirection: 'column', alignItems: 'center', marginTop: '12px' }}>
              <Button
                variant="outlined"
                type="button"
                onClick={() => toggleReview(order._id)}
                sx={{ borderRadius: '8px', textTransform: 'none' }}
              >
                {reviewOpenStates[order._id] ? t('hideReview') : t('viewReview')}
              </Button>
              {reviewOpenStates[order._id] && (
                <Box sx={{ mt: 1.5, p: 1.5, width: '100%', border: `1px solid ${theme.palette.component.borderColor}`, borderRadius: 1.5 }}>
                  <Typography variant="body2">{order.customerFeedback.text}</Typography>
                </Box>
              )}
            </Box>
          )}
        </div>
      ))}
    </Box>
  );
};


export default OrdersComponentController;